import { gridIdForZara } from "src/api/services/search/searchApi";
import { ROUTE_PATHS } from "../constants";
import { ORIGIN_PARAM_NAME } from "src/constants/UrlConstants";
import { getPathFragment } from "../BasePathTools";

export interface UrlDetailsFromZara {
  language: string;
  param1: string; 
  param2: string; 
  productId?: string | null;
}
const DOMAIN_PATTERN = '(?:(?:https?:)?(?://)?(?:www\\.)?[^/]+)?';
const UNIVERSE_PATTERN = '(woman|man|kid)';

export const URL_PATTERNS = {

  base: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/([^/]+)\\/([^/]+)(\\/([^/]+))?$`),

  simpleBase: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/([^/]+)$`),

  productDetail: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/products\\/detail\\/([^/]+)$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/product-detail\\/([^/]+)$`)
  ],
  
  orders: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/account\\/orders$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/my-purchases$`)
  ],
  
  mySells: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/account\\/my-sells$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/my-sales$`)
  ],
  
  askForDetails: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/products\\/ask-details\\/([^/]+)$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/communication\\/ask-details\\/([^/]+)$`)
  ],
  
  communication: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/communication\\/(?!ask-details)([^/]+)$`)
  ],
  
  cart: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/user\\/cart(?:\\?origin=[^&]*)?$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/user\\/cart$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/cart(?:\\?origin=[^&]*)?$`)
  ],
  
  profile: [
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/user\\/profile(?:\\?origin=[^&]*)?$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/user\\/profile$`),
    new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/profile(?:\\?origin=[^&]*)?$`)
  ],

  extractId: {
    productDetailNew: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/product-detail\\/([^/]+)$`),
    communicationDirect: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/communication\\/(?!ask-details)([^/]+)$`),
    default: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/(products|communication)\\/(detail|ask-details)\\/([^/]+)$`)
  },

  categoryFromZara: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/redirect\\/${UNIVERSE_PATTERN}\\/([^/]+)$`),

  cartFromPreowned: new RegExp(`^${DOMAIN_PATTERN}\\/([^/]+)\\/([^/]+)\\/preowned-resell\\/cart$`),

  extractDetails: /^(?:(?:https?:)?(?:\/\/)?(?:www\.)?[^/]+)?\/([^/]+)\/([^/]+)\/preowned-resell\/redirect\/([^/]+)\/([^/]+)(\/([^/]+))?(?:#.*)?$/
};

const matchesAnyPattern = (patterns: RegExp[]): boolean => {
  return patterns.some(pattern => pattern.test(window.location.pathname));
};

const TestPathRegex = (pathRegex: RegExp): boolean => {
  
  return pathRegex.test(window.location.pathname) || pathRegex.test(window.location.href);
};

export async function redirectToPreownedResell(
  marketData: any,
  urlParamsFromZara: any,
  section: string = '',
  productId?: string | null,
): Promise<string> {
  let path = '';
  const country = marketData?.countryIso?.toLowerCase();
  const url = new URL(window.location.href); 
  const basePath = `${url.origin}/${country}/${urlParamsFromZara.language}/preowned-resell/`;

  switch (section) {
    case 'product-detail':
      path = `${ROUTE_PATHS.PRODUCT_DETAIL}${productId ? `/${productId}` : ''}`;
      break;
    case 'ask-for-details':
      path = `${ROUTE_PATHS.ASK_FOR_DETAILS}${productId ? `/${productId}` : ''}`;
      break;
    case 'communication':
      path = `${ROUTE_PATHS.COMMUNICATION}${productId ? `/${productId}` : ''}`;
      break;
    case 'orders':
      path = `${ROUTE_PATHS.MY_PURCHASES}`;
      break;
    case 'my-sells':
      path = `${ROUTE_PATHS.MY_SALES}`;
      break;
    case 'profile':
      path = `${ROUTE_PATHS.PROFILE}${productId?`?${ORIGIN_PARAM_NAME}=${productId}`:''}`;
      break;
    case 'cart':
      path = `${ROUTE_PATHS.CART}${productId?`?${ORIGIN_PARAM_NAME}=${productId}`:''}`;
      break;
    default:
      const parcialUrlToRedirect = await gridIdForZara({
        storeId: marketData.resellStoreId,
        universeCode: urlParamsFromZara.param1,
        gridCode: urlParamsFromZara.param2, 
        lang: urlParamsFromZara.language,
      });
      const defaultUrlForRedirect = getPathFragment(parcialUrlToRedirect);
      if (defaultUrlForRedirect) {
        path = defaultUrlForRedirect;
      }else{
        path = `${ROUTE_PATHS.PRODUCTS}`
      }
      break;
  }
 
  return path;
}


export const isUserFromZaraMenu = (): boolean => {
  return TestPathRegex(URL_PATTERNS.base) || TestPathRegex(URL_PATTERNS.simpleBase);
};

export const isUserFromCategoryMenu = (): boolean => {
  return TestPathRegex(URL_PATTERNS.categoryFromZara);
};

export const isCartFromPreownedPage = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasOriginParam = urlParams.has(ORIGIN_PARAM_NAME);
  const matchesPattern = URL_PATTERNS.cartFromPreowned.test(window.location.pathname);

  return matchesPattern && !hasOriginParam;
};

export const extractDetailsFromZaraUrl = (): UrlDetailsFromZara | null => {
  const url = window.location.href;
  const path = window.location.pathname;
  
  const match = url?.match(URL_PATTERNS.extractDetails);
  if (match) {
    const [, , language, param1, param2, , productId] = match;
    return { language, param1, param2, productId };
  }
  
  const simpleMatch = path.match(URL_PATTERNS.simpleBase);
  if (simpleMatch) {
    const language = simpleMatch[2];
    const section = simpleMatch[3];
    
    let param1: string = 'default';
    let param2: string = section;
    
    if (section === 'my-purchases') {
      param1 = 'account';
      param2 = 'orders';
    } else if (section === 'my-sales') {
      param1 = 'account';
      param2 = 'my-sells';
    } else if (section === 'product-detail') {
      param1 = 'products';
      param2 = 'detail';
    }
    
    return { language, param1, param2, productId: null };
  }
  
  return null;
};

export const isProductDetailPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.productDetail);
};

export const isOrdersPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.orders);
};

export const isMySellsPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.mySells);
};

export const isAskForDetailsPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.askForDetails);
};

export const isCommunicationPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.communication);
};

export const isProfilePage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.profile);
};

export const isCartPage = (): boolean => {
  return matchesAnyPattern(URL_PATTERNS.cart);
};

export const extractProductIdFromUrl = (): string | null => {
  const { pathname, href } = window.location;
  
  const productDetailMatch = pathname.match(URL_PATTERNS.extractId.productDetailNew);
  if (productDetailMatch) {
    return productDetailMatch[3];
  }
  
  const fullUrlProductDetailMatch = href.match(URL_PATTERNS.extractId.productDetailNew);
  if (fullUrlProductDetailMatch) {
    return fullUrlProductDetailMatch[3];
  }
  const communicationMatch = pathname.match(URL_PATTERNS.extractId.communicationDirect);
  if (communicationMatch) {
    return communicationMatch[3];
  }
  
  const defaultMatch = pathname.match(URL_PATTERNS.extractId.default);
  if (defaultMatch) {
    return defaultMatch[5];
  }
  
  return null;
};

export const extractCommunicationIdFromUrl = (): string | null => {
  const communicationMatch = window.location.pathname.match(URL_PATTERNS.communication[0]);
  return communicationMatch ? communicationMatch[3] : null;
};

/*
  REGEXS PARA REDIRECCIONES AL ENTRAR A LA APLICACIÓN DESDE ZARA (CON URL)
  
  default (redirección a parrillas como antes): 
    - /fr/fr/preowned-resell/redirect/kids/default
    - http://localhost:3000/fr/fr/preowned-resell/redirect/kids/default
  
  askfordetails: 
    - /fr/fr/preowned-resell/redirect/products/ask-details/518060
    - http://localhost:3000/fr/fr/preowned-resell/redirect/products/ask-details/518060
    - /fr/fr/preowned-resell/redirect/communication/ask-details/518060
    - http://localhost:3000/fr/fr/preowned-resell/redirect/communication/ask-details/518060
  
  product-detail: 
    - /fr/fr/preowned-resell/redirect/products/detail/518060
    - http://localhost:3000/fr/fr/preowned-resell/redirect/products/detail/518060
    - /fr/fr/preowned-resell/redirect/product-detail/518060
    - http://localhost:3000/fr/fr/preowned-resell/redirect/product-detail/518060
  
  my-sales: 
    - /fr/fr/preowned-resell/redirect/account/my-sells
    - http://localhost:3000/fr/fr/preowned-resell/redirect/account/my-sells
    - /fr/fr/preowned-resell/redirect/my-sales
    - http://localhost:3000/fr/fr/preowned-resell/redirect/my-sales
  
  my-purchases: 
    - /fr/fr/preowned-resell/redirect/account/orders
    - http://localhost:3000/fr/fr/preowned-resell/redirect/account/orders
    - /fr/fr/preowned-resell/redirect/my-purchases
    - http://localhost:3000/fr/fr/preowned-resell/redirect/my-purchases
  
  communication:
    - /fr/fr/preowned-resell/redirect/communication/654321
    - http://localhost:3000/fr/fr/preowned-resell/redirect/communication/654321
  
  cart:
    - /fr/fr/preowned-resell/redirect/user/cart?origin=menu
    - http://localhost:3000/fr/fr/preowned-resell/redirect/user/cart?origin=menu
    - /fr/fr/preowned-resell/redirect/user/cart
    - http://localhost:3000/fr/fr/preowned-resell/redirect/user/cart
    - /fr/fr/preowned-resell/cart?origin=menu
    - http://localhost:3000/fr/fr/preowned-resell/cart?origin=menu
  
  profile:
    - /fr/fr/preowned-resell/redirect/user/profile?origin=orders
    - http://localhost:3000/fr/fr/preowned-resell/redirect/user/profile?origin=orders
    - /fr/fr/preowned-resell/redirect/user/profile
    - http://localhost:3000/fr/fr/preowned-resell/redirect/user/profile
    - /fr/fr/preowned-resell/profile?origin=orders
    - http://localhost:3000/fr/fr/preowned-resell/profile?origin=orders
*/